import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { isValidElement, ReactNode } from 'react';

import { cn } from '@/utils/classname';

interface TooltipProps {
  title: ReactNode | string;
  children: ReactNode;
  placement?:
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delay?: number;
  tooltipClassName?: string;
}

const sideMap: Record<string, 'top' | 'right' | 'bottom' | 'left'> = {
  top: 'top',
  'top-start': 'top',
  'top-end': 'top',
  bottom: 'bottom',
  'bottom-start': 'bottom',
  'bottom-end': 'bottom',
  left: 'left',
  'left-start': 'left',
  'left-end': 'left',
  right: 'right',
  'right-start': 'right',
  'right-end': 'right',
};

const alignMap: Record<string, 'start' | 'center' | 'end'> = {
  top: 'center',
  'top-start': 'start',
  'top-end': 'end',
  bottom: 'center',
  'bottom-start': 'start',
  'bottom-end': 'end',
  left: 'center',
  'left-start': 'start',
  'left-end': 'end',
  right: 'center',
  'right-start': 'start',
  'right-end': 'end',
};

export function Tooltip(props: TooltipProps) {
  const {
    placement = 'bottom',
    children,
    title,
    open,
    onOpenChange,
    defaultOpen,
    delay = 100,
    tooltipClassName,
  } = props;

  const side = sideMap[placement] ?? 'bottom';
  const align = alignMap[placement] ?? 'center';

  if (!title) return <>{children}</>;

  // Radix asChild requires a single valid React element as the trigger.
  // Wrap non-element children (strings, fragments, etc.) in a span.
  const trigger = isValidElement(children) ? children : <span>{children}</span>;

  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <TooltipPrimitive.Root
        open={open}
        onOpenChange={onOpenChange}
        defaultOpen={defaultOpen}
        delayDuration={delay}
      >
        <TooltipPrimitive.Trigger
          asChild
          data-testid="tooltip-trigger"
          data-tooltip-title={title}
        >
          {trigger}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={5}
            data-testid="tooltip-content"
            className={cn(
              // Shape - rounded corners for modern look
              'rounded-md',
              // Background - design system colors with dark theme support
              'bg-chariot-background-primary',
              '[data-theme="dark"]:bg-opacity-95 [data-theme="dark"]:backdrop-blur-xs',
              // Border - subtle definition
              'border-chariot-stroke border',
              // Padding - more breathing room
              'p-3',
              // Typography - design system text colors
              'text-chariot-text-primary text-xs',
              // Elevation - enhanced shadow for depth
              'ring-opacity-5 shadow-xl ring-1 ring-black',
              // z-index for table visibility
              'z-[9999]',
              // Animations
              'animate-in fade-in-0 zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              tooltipClassName
            )}
          >
            {title}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
